import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clientsApi, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ShieldAlert, ArrowLeft, Contact, UserPlus, Phone, Mail, Home, LifeBuoy,
  Trash2, Pencil, Loader2, X, UserRound, RefreshCw, Smartphone,
} from "lucide-react";

const empty = {
  first_name: "", last_name: "", phone: "", email: "", address: "",
  emergency_contact_name: "", emergency_contact_phone: "",
};

const Clients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState("");
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try { const list = await clientsApi.list(); setClients(list); return list; }
    catch { toast.error("Impossible de charger les clients"); return []; }
    finally { setLoading(false); }
  };

  const digits = (p) => (p || "").replace(/\D/g, "").slice(-9);

  const syncFromApp = async (localList) => {
    setSyncing(true);
    try {
      const [hist, act] = await Promise.all([
        api.history().catch(() => []),
        api.activeAlerts().then((d) => d.alerts || []).catch(() => []),
      ]);
      const seen = new Set((localList || []).map((c) => digits(c.phone)));
      const uniq = new Map();
      [...act, ...hist].forEach((a) => {
        const d = digits(a.user_phone);
        if (d && !seen.has(d) && !uniq.has(d)) uniq.set(d, { name: a.user_name, phone: a.user_phone });
      });
      let created = 0;
      for (const v of uniq.values()) {
        try { const r = await clientsApi.importFromApp(v); if (r.created) created++; } catch { /* skip */ }
      }
      if (created > 0) { toast.success(`${created} client(s) importé(s) depuis l'app mobile`); load(); }
    } catch { /* ignore */ } finally { setSyncing(false); }
  };

  useEffect(() => { load().then((list) => syncFromApp(list)); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const resetForm = () => { setForm(empty); setEditId(null); };

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({
      first_name: c.first_name || "", last_name: c.last_name || "", phone: c.phone || "",
      email: c.email || "", address: c.address || "",
      emergency_contact_name: c.emergency_contact_name || "",
      emergency_contact_phone: c.emergency_contact_phone || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim()) {
      return toast.error("Nom, prénom et téléphone sont obligatoires");
    }
    setSaving(true);
    try {
      if (editId) {
        await clientsApi.update(editId, form);
        toast.success("Client mis à jour");
      } else {
        await clientsApi.create(form);
        toast.success(`Client ${form.first_name} ${form.last_name} ajouté`);
      }
      resetForm();
      load();
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    setRemoving(c.id);
    try {
      await clientsApi.remove(c.id);
      toast.success("Client supprimé");
      setClients((prev) => prev.filter((x) => x.id !== c.id));
      if (editId === c.id) resetForm();
    } catch { toast.error("Échec de la suppression"); }
    finally { setRemoving(""); }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100">
      <header className="h-16 sticky top-0 z-40 backdrop-blur-md bg-slate-900/80 border-b border-slate-800 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-950 border border-emerald-500/30 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="font-display font-bold leading-tight flex items-center gap-2"><Contact className="w-4 h-4 text-emerald-400" /> Fichier clients</h1>
            <p className="text-[11px] font-mono text-slate-500">Sama Jaamu — Citoyens enregistrés</p>
          </div>
        </div>
        <Button data-testid="clients-back-btn" onClick={() => navigate("/")} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour au dashboard
        </Button>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
        <section className="lg:col-span-2">
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 sticky top-24">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-semibold text-lg flex items-center gap-2">
                {editId ? <Pencil className="w-5 h-5 text-yellow-400" /> : <UserPlus className="w-5 h-5 text-emerald-400" />}
                {editId ? "Modifier le client" : "Ajouter un client"}
              </h2>
              {editId && (
                <button data-testid="client-cancel-edit" onClick={resetForm} className="text-slate-400 hover:text-slate-200"><X className="w-4 h-4" /></button>
              )}
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Prénom"><Input data-testid="client-firstname-input" value={form.first_name} onChange={set("first_name")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="Awa" /></Field>
                <Field label="Nom"><Input data-testid="client-lastname-input" value={form.last_name} onChange={set("last_name")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="Diop" /></Field>
              </div>
              <Field label="Téléphone"><Input data-testid="client-phone-input" value={form.phone} onChange={set("phone")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="+221 77 123 45 67" /></Field>
              <Field label="Adresse e-mail"><Input data-testid="client-email-input" type="email" value={form.email} onChange={set("email")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="awa.diop@email.sn" /></Field>
              <Field label="Adresse"><Input data-testid="client-address-input" value={form.address} onChange={set("address")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="Rue 10, Médina, Dakar" /></Field>
              <div className="pt-2 border-t border-slate-800">
                <p className="text-xs font-mono uppercase tracking-wider text-amber-400 flex items-center gap-1.5 mb-3"><LifeBuoy className="w-3.5 h-3.5" /> Contact d'urgence</p>
                <div className="space-y-3">
                  <Field label="Nom du contact"><Input data-testid="client-ec-name-input" value={form.emergency_contact_name} onChange={set("emergency_contact_name")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="Fatou Diop" /></Field>
                  <Field label="Téléphone du contact"><Input data-testid="client-ec-phone-input" value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} className="bg-slate-950 border-slate-700 text-slate-100 h-10" placeholder="+221 76 000 00 00" /></Field>
                </div>
              </div>
              <Button type="submit" data-testid="client-save-btn" disabled={saving} className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? "Enregistrer les modifications" : "Enregistrer le client")}
              </Button>
            </form>
          </div>
        </section>

        <section className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-semibold text-lg">Clients enregistrés</h2>
            <div className="flex items-center gap-3">
              <Button data-testid="clients-sync-btn" onClick={() => syncFromApp(clients)} disabled={syncing} size="sm" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 h-8">
                {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin sm:mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 sm:mr-1.5" />}
                <span className="hidden sm:inline">Synchroniser l'app</span>
              </Button>
              <span className="font-mono text-xs text-slate-500" data-testid="clients-count">{clients.length} client(s)</span>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : clients.length === 0 ? (
            <div className="bg-slate-900/60 border border-dashed border-slate-700 rounded-2xl p-10 text-center text-slate-500">
              <Contact className="w-8 h-8 mx-auto mb-3 text-slate-600" />
              <p className="font-display font-semibold text-slate-300">Aucun client enregistré</p>
              <p className="text-sm mt-1">Ajoutez un citoyen via le formulaire. Ses infos s'afficheront automatiquement lors d'une alerte.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="clients-list">
              {clients.map((c) => (
                <div key={c.id} data-testid={`client-row-${c.id}`} className="bg-slate-900/80 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
                        <UserRound className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-display font-semibold text-slate-100 truncate flex items-center gap-2">
                          {c.first_name} {c.last_name}
                          {c.source === "mobile" && (
                            <span className="text-[9px] font-mono bg-blue-500/10 text-blue-400 border border-blue-500/30 px-1.5 py-0.5 rounded-full flex items-center gap-1 shrink-0"><Smartphone className="w-2.5 h-2.5" /> App mobile</span>
                          )}
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs font-mono text-slate-400 mt-1">
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-emerald-500" /> {c.phone}</span>
                          {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-slate-500" /> {c.email}</span>}
                        </div>
                        {c.address && <p className="text-xs font-mono text-slate-500 flex items-center gap-1 mt-0.5"><Home className="w-3 h-3" /> {c.address}</p>}
                        {(c.emergency_contact_name || c.emergency_contact_phone) && (
                          <p className="text-xs font-mono text-amber-400/90 flex items-center gap-1 mt-1"><LifeBuoy className="w-3 h-3" /> Urgence : {c.emergency_contact_name} {c.emergency_contact_phone && `· ${c.emergency_contact_phone}`}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button data-testid={`client-edit-${c.id}`} onClick={() => startEdit(c)} size="icon" variant="ghost" className="text-slate-400 hover:text-yellow-400 hover:bg-yellow-950/30"><Pencil className="w-4 h-4" /></Button>
                      <Button data-testid={`client-delete-${c.id}`} onClick={() => remove(c)} disabled={removing === c.id} size="icon" variant="ghost" className="text-slate-400 hover:text-red-400 hover:bg-red-950/40">
                        {removing === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">{label}</Label>
    {children}
  </div>
);

export default Clients;
